import { useState, useEffect, useMemo } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { useQueryClient, QueryClient } from '@tanstack/react-query';

import { TokenFail } from '@/components/common/TokenFail';
import { PopupType, UserState } from '@/types';
import { useUserState } from '@/hooks/useUserState';
import { ctxUser } from '@/contexts/UserContext';
import { HandleErrors } from './HandleErrors';
import { checkCookiebot } from '@/utilities/checkCookiebot';
import { LoginPage, PleaseWait } from '@/components';
import { useNotificationStore } from '@/stores/notificationstore';
import ResetPasswordPage from '@/components/login/ResetPasswordPage';
import ChangePasswordPage from '@/components/login/ChangePasswordPage';
import { Layout } from './Layout';
import { getAdditionalElements, popupDefs } from './popups';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { User } from '@/tmp/User';

async function closePopup() {
  // itt nincs route context :(
  history.back();
}

function getPopups(client: QueryClient, isPhone: boolean, user: User) {
  const popups = popupDefs({
    closePopup,
    client,
    isBottom: isPhone,
    user,
  });

  // children csak a menustruktura szempontjabol szamit
  // route definicional ugyanolyan
  let allChildren: PopupType[] = []; // most csak 1 melysegu, de lehetne rekurziv
  popups.forEach((p) => {
    if (p.children?.length) allChildren = allChildren.concat(p.children);
  });

  const additionalElement = getAdditionalElements(closePopup, true, true);
  additionalElement.forEach((p) => {
    if (p.children?.length) allChildren = allChildren.concat(p.children);
  });

  const allPopups = popups.concat(additionalElement).concat(allChildren);
  return { popups, allPopups };
}

function RenderLogin({ loggedIn, pwReset }: { loggedIn: (user: UserState) => void; pwReset: () => void }) {
  return (
    <HandleErrors>
      <LoginPage loggedIn={loggedIn} resetPw={pwReset} />
    </HandleErrors>
  );
}

function RenderApp({ user }: { user: User }) {
  const client = useQueryClient();
  const [isPhone, setPhone] = useState<boolean>(isPhoneOrHighScreen());

  useEffect(() => {
    function handleResize() {
      if (document.readyState !== 'complete') {
        setTimeout(handleResize, 100);
        return;
      }
      setPhone(isPhoneOrHighScreen());
    }

    setTimeout(handleResize, 100);

    // TODO lehetne mediaquery
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const { popups, allPopups } = useMemo(() => getPopups(client, isPhone, user), [client, isPhone, user]);

  function defaultRoute() {
    return isPhone ? '/tracking/select' : '/tracking';
  }

  function buildTree(level: number, prefix: string) {
    if (level >= 4) return null;

    function samePrefix(path: string) {
      const pos = path.lastIndexOf('/');
      if (pos <= 0) return true;
      return path.startsWith(prefix);
    }

    function getPath(path: string) {
      const pos = path.lastIndexOf('/');
      if (pos >= 0) return path;
      else return prefix + '/' + path;
    }

    function removeLeadingSlash(path: string) {
      if (path.startsWith('/')) return path.substring(1);
      return path;
    }

    function buildPrefix(prefix: string, path: string) {
      if (path.startsWith(prefix)) return path;
      return prefix + '/' + removeLeadingSlash(path);
    }

    const result = allPopups.filter((r) => r.level === level).filter((r) => samePrefix(r.path));

    return result.map((p) => (
      <Route path={getPath(p.path)} element={p.element}>
        {buildTree(level + 1, buildPrefix(prefix, p.path))}
      </Route>
    ));
  }

  return (
    <HandleErrors>
      <BrowserRouter basename={BASE}>
        <Routes>
          <Route path="/" element={<Layout isPhone={isPhone} popups={popups} />}>
            {buildTree(1, '')}
          </Route>
          <Route index element={<Navigate replace to={defaultRoute()} />} />
          <Route path="*" element={<Navigate replace to={defaultRoute()} />} />
        </Routes>
      </BrowserRouter>
    </HandleErrors>
  );
}

function App() {
  const params = new URLSearchParams(document.location.search);
  const [user, setUser] = useUserState(undefined, params);
  const [trackingAllowed, setTrackingAllowed] = useState<boolean | undefined>(undefined);
  const setNotificationsAllowed = useNotificationStore((state) => state.setNotificationsAllowed);

  useEffect(() => {
    function onCookiebotDialog() {
      const cooikebotDialog = document.getElementById('CybotCookiebotDialog');
      if (cooikebotDialog) {
        cooikebotDialog.remove();
      }
    }

    window.addEventListener('CookiebotOnAccept', onCookiebotDialog);
    window.addEventListener('CookiebotOnDecline', onCookiebotDialog);

    return () => {
      window.removeEventListener('CookiebotOnAccept', onCookiebotDialog);
      window.removeEventListener('CookiebotOnDecline', onCookiebotDialog);
    };
  }, []);

  useEffect(() => {
    window.allowNotifications = setNotificationsAllowed;
    return () => {
      window.allowNotifications = () => {};
    };
  }, [setNotificationsAllowed]);

  useEffect(() => {
    if (!window.userConsent && !window.flutter_inappwebview) {
      return;
    }

    window.updateUserConsent = function (allowed: string) {
      setTrackingAllowed(allowed === 'true');
    };

    function onCookiebotDialog() {
      if (!window.userConsent && !window.flutter_inappwebview) {
        return;
      }
      if (trackingAllowed) {
        return;
      }
      if (!checkCookiebot()) {
        return;
      }
      Cookiebot.hide();
    }

    window.addEventListener('CookiebotOnDialogDisplay', onCookiebotDialog);

    if (trackingAllowed === undefined) {
      if (window.userConsent) {
        window.userConsent.postMessage('');
      }
      if (window.flutter_inappwebview) {
        window.flutter_inappwebview.callHandler('userConsent', '');
      }
    } else {
      try {
        if (!checkCookiebot()) {
          return;
        }
        if (trackingAllowed) {
          if (!Cookiebot.hasResponse) {
            Cookiebot.show();
          }
        } else {
          if (Cookiebot.hasResponse) {
            Cookiebot.withdraw();
          } else {
            Cookiebot.hide();
          }
        }
      } catch (e) {
        console.error('Cannot update Cookiebot. TrackingAllowed = ', trackingAllowed, e);
      }
    }
    return () => {
      window.removeEventListener('CookiebotOnDialogDisplay', onCookiebotDialog);
    };
  }, [trackingAllowed]);

  function loggedIn(user: UserState) {
    setUser(user);
  }

  function pwReset() {
    setUser('password_reset');
  }

  function userReset() {
    setUser(null);
    window.history.replaceState(null, document.title, '/');
  }

  switch (user) {
    case undefined:
      return <PleaseWait />;
    case null:
      return <RenderLogin loggedIn={loggedIn} pwReset={pwReset} />;
    case 'token_failure':
      return <TokenFail />;
    case 'password_reset':
      return <ResetPasswordPage returnLoginPage={userReset} />;
    case 'change_password':
      return (
        <ChangePasswordPage
          successfulChange={userReset}
          user={params.get('user') ?? ''}
          hash={params.get('hash') ?? ''}
        />
      );
    default:
      return (
        <ctxUser.Provider
          value={{
            user,
            isToken: () => user.isTokenUser,
            hasExtraFeature: (extraFeature: string) => user.extraFeatures?.includes(extraFeature),
            logout: () => setUser(null),
          }}
        >
          <RenderApp user={user} />
        </ctxUser.Provider>
      );
  }
}

export default App;
